import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getArAging,
  getInvoices,
  listPaymentPlans,
} from '../services/paymentsService.js';
import LedgerTab from './LedgerTab.js';
import KpiTile from './KpiTile';
import Icon from './Icon';

// Bucket column order + labels. Kept in the component (rather than trusting
// the snapshot's bucketKeys blindly) so the table layout is stable even if a
// snapshot predates a bucket rename.
const BUCKETS = [
  { key: 'd0_30', label: '0–30 days' },
  { key: 'd31_60', label: '31–60 days' },
  { key: 'd61_90', label: '61–90 days' },
  { key: 'd90_plus', label: '90+ days' },
];

const formatCurrency = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

const formatDate = (iso) => {
  if (!iso) return '—';
  // Snapshot asOf is a YYYY-MM-DD practice-local date; render without TZ shift.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return iso;
};

const sumBuckets = (buckets) =>
  BUCKETS.reduce((acc, b) => acc + Number((buckets || {})[b.key] || 0), 0);

const pct = (part, whole) => {
  const w = Number(whole || 0);
  if (w <= 0) return 0;
  return Math.round((Number(part || 0) / w) * 100);
};

// ---------------------------------------------------------------------------
// Invoice indicator + timeline (mirrors InvoiceTab's activityLog semantics)
// ---------------------------------------------------------------------------

const DEAD_INVOICE_STATES = new Set(['cancelled', 'superseded']);

const INVOICE_ACTION_LABELS = {
  invoice_created: 'Invoice Created',
  sms_sent: 'SMS Sent',
  email_sent: 'Email Sent',
  receipt_emailed: 'Receipt Emailed',
  link_copied: 'Link Copied',
  sent_to_device: 'Sent to Device',
  save_card_on_device: 'Save Card on Device',
  card_on_file_charged: 'Card on File Charged',
  payment_processing: 'Payment Processing',
  payment_processing_sms_sent: 'Processing SMS Sent',
  payment_completed: 'Payment Completed',
  payment_completed_sms_sent: 'Payment SMS Sent',
  payment_failed: 'Payment Failed',
  invoice_superseded: 'Invoice Superseded',
  invoice_cancelled: 'Invoice Cancelled',
  replaced_pending_invoice: 'Replaced Pending Invoice',
  plan_card_repointed: 'Plan Card Updated',
};

const getActionLabel = (action) => {
  if (INVOICE_ACTION_LABELS[action]) return INVOICE_ACTION_LABELS[action];
  return String(action || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const dotColorForAction = (action) => {
  if (action === 'invoice_created' || action === 'payment_completed') return '#1d6b40';
  if (action === 'payment_processing') return '#d9901a';
  if (action === 'payment_failed') return '#b42318';
  if (action === 'invoice_superseded' || action === 'invoice_cancelled') return '#6b716d';
  return '#1f5a3d';
};

// Colour + label for an invoice status pill.
const STATUS_META = {
  paid: { label: 'Paid', color: '#1d6b40', bg: '#e7f4ec' },
  processing: { label: 'Processing', color: '#a8520c', bg: '#fdf3e3' },
  pending: { label: 'Pending', color: '#1f4e79', bg: '#e8f0f7' },
  failed: { label: 'Failed', color: '#912018', bg: '#fdecea' },
  cancelled: { label: 'Cancelled', color: '#3c4540', bg: '#eef0ee' },
  superseded: { label: 'Superseded', color: '#3c4540', bg: '#eef0ee' },
};
const statusMeta = (status) =>
  STATUS_META[String(status || '').toLowerCase()]
  || { label: status || 'Unknown', color: '#3c4540', bg: '#eef0ee' };

// Reconstruct the invoice timeline from activityLog + synthesized state
// transitions, matching what InvoiceTab renders.
const buildInvoiceTimeline = (invoice) => {
  const log = Array.isArray(invoice.activityLog) ? invoice.activityLog : [];
  const has = (a) => log.some((e) => e.action === a);
  const status = String(invoice.status || '').toLowerCase();

  const timeline = has('invoice_created')
    ? [...log]
    : [{ action: 'invoice_created', timestamp: invoice.createdAt, details: {} }, ...log];

  if ((status === 'processing' || status === 'paid') && !has('payment_processing')) {
    const hooks = Array.isArray(invoice.payinWebhooks) ? invoice.payinWebhooks : [];
    const processingHook = hooks.find((w) => w.event_type === 'payin.processing');
    const ts = invoice.processingAt
      || (processingHook && (processingHook?.data?.data?.updated_at || processingHook.received_at))
      || invoice.updatedAt;
    timeline.push({ action: 'payment_processing', timestamp: ts, details: {} });
  }
  if (status === 'paid' && !has('payment_completed')) {
    timeline.push({
      action: 'payment_completed',
      timestamp: invoice.paidAt || invoice.updatedAt,
      details: {},
    });
  }
  if (!has('payment_failed') && (invoice.refusalCode || invoice.failedAt)) {
    timeline.push({
      action: 'payment_failed',
      timestamp: invoice.failedAt || invoice.updatedAt,
      details: {
        refusalCode: invoice.refusalCode,
        refusalDescription: invoice.refusalDescription,
      },
    });
  }
  timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  return timeline;
};

// Pick the pill colour for a household's invoice badge: prefer the most
// "advanced" live status, ignoring cancelled/superseded unless that's all
// there is.
const badgeMetaForInvoices = (invoices) => {
  if (!invoices || invoices.length === 0) return null;
  const live = invoices.filter(
    (i) => !DEAD_INVOICE_STATES.has(String(i.status || '').toLowerCase())
  );
  const pool = live.length ? live : invoices;
  const statuses = pool.map((i) => String(i.status || '').toLowerCase());
  let color = '#6b716d';
  if (statuses.includes('paid')) color = '#1d6b40';
  else if (statuses.includes('processing')) color = '#a8520c';
  else if (statuses.includes('pending')) color = '#1f5a3d';
  return { count: invoices.length, color };
};

// ---------------------------------------------------------------------------
// Payment-plan indicator + detail (mirrors the invoice badge/modal)
// ---------------------------------------------------------------------------

const PLAN_ACTION_LABELS = {
  enrolled: 'Enrolled',
  installment_invoice_provisioned: 'Installment Scheduled',
  installment_charge_attempted: 'Installment Charge Attempted',
  installment_charge_succeeded: 'Installment Paid',
  installment_charge_failed: 'Installment Failed',
  paused: 'Plan Paused',
  resumed: 'Plan Resumed',
  cancelled: 'Plan Cancelled',
  defaulted: 'Plan Defaulted',
  completed: 'Plan Completed',
  card_changed: 'Card Updated',
};

const getPlanActionLabel = (action) => {
  if (PLAN_ACTION_LABELS[action]) return PLAN_ACTION_LABELS[action];
  return String(action || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const planDotColor = (action) => {
  if (action === 'installment_charge_succeeded' || action === 'completed' || action === 'enrolled') return '#1d6b40';
  if (action === 'installment_charge_failed' || action === 'defaulted' || action === 'cancelled') return '#b42318';
  if (action === 'paused') return '#d9901a';
  return '#1f5a3d';
};

const PLAN_STATUS_META = {
  active: { label: 'Active', color: '#1d6b40', bg: '#e7f4ec' },
  paused: { label: 'Paused', color: '#a8520c', bg: '#fdf3e3' },
  defaulted: { label: 'Defaulted', color: '#912018', bg: '#fdecea' },
  completed: { label: 'Completed', color: '#3c4540', bg: '#eef0ee' },
  cancelled: { label: 'Cancelled', color: '#3c4540', bg: '#eef0ee' },
};
const planStatusMeta = (status) =>
  PLAN_STATUS_META[String(status || '').toLowerCase()]
  || { label: status || 'Unknown', color: '#3c4540', bg: '#eef0ee' };

const DEAD_PLAN_STATES = new Set(['completed', 'cancelled']);

// Pill colour for the plan badge: an active plan wins, then paused, then
// defaulted; dead (completed/cancelled) plans only colour it if that's all.
const badgeMetaForPlans = (plans) => {
  if (!plans || plans.length === 0) return null;
  const live = plans.filter((p) => !DEAD_PLAN_STATES.has(String(p.status || '').toLowerCase()));
  const pool = live.length ? live : plans;
  const statuses = pool.map((p) => String(p.status || '').toLowerCase());
  let color = '#6b716d';
  if (statuses.includes('active')) color = '#1d6b40';
  else if (statuses.includes('paused')) color = '#a8520c';
  else if (statuses.includes('defaulted')) color = '#912018';
  return { count: plans.length, color };
};

const centsToUsd = (cents) => formatCurrency((Number(cents) || 0) / 100);

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const th = {
  textAlign: 'right',
  padding: '10px 12px',
  fontSize: '0.78rem',
  color: '#6b716d',
  fontWeight: 600,
  borderBottom: '2px solid #e3e7e4',
  whiteSpace: 'nowrap',
};
const thLeft = { ...th, textAlign: 'left' };
const td = {
  textAlign: 'right',
  padding: '10px 12px',
  fontSize: '0.9rem',
  color: '#16241c',
  borderBottom: '1px solid #eef0ee',
  whiteSpace: 'nowrap',
};
const tdLeft = { ...td, textAlign: 'left' };

// ---------------------------------------------------------------------------
// Aging bar chart (Insurance AR vs Patient AR × buckets)
// ---------------------------------------------------------------------------

const compactCurrency = (n) => {
  const v = Number(n || 0);
  if (v <= 0) return '$0';
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
};

const AGING_SERIES = [
  { key: 'insuranceArBuckets', label: 'Insurance AR', color: '#a8520c' },
  { key: 'patientArBuckets', label: 'Patient AR', color: '#b42318' },
];

const CHART_HEIGHT = 170;

const AgingBarChart = ({ totals }) => {
  const insBuckets = totals.insuranceArBuckets || {};
  const patBuckets = totals.patientArBuckets || {};

  // Scale every bar off the single tallest bar so the chart fills its height.
  const maxVal = Math.max(
    1,
    ...BUCKETS.flatMap((b) => [
      Number(insBuckets[b.key] || 0),
      Number(patBuckets[b.key] || 0),
    ])
  );
  const barPx = (v) => Math.round((Number(v || 0) / maxVal) * CHART_HEIGHT);

  return (
    <div style={{ border: '1px solid #e3e7e4', borderRadius: 12, backgroundColor: '#fff', padding: '18px 20px' }}>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 18, marginBottom: 16 }}>
        {AGING_SERIES.map((s) => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: s.color, display: 'inline-block' }} />
            <span style={{ fontSize: '0.8rem', color: '#3c4540', fontWeight: 600 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Bars */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, minWidth: 420 }}>
        {BUCKETS.map((b) => {
          const ins = Number(insBuckets[b.key] || 0);
          const pat = Number(patBuckets[b.key] || 0);
          return (
            <div key={b.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 6, height: CHART_HEIGHT + 18, width: '100%' }}>
                {[{ v: ins, c: AGING_SERIES[0].color }, { v: pat, c: AGING_SERIES[1].color }].map((bar, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', flex: 1, maxWidth: 46 }}>
                    <div style={{ fontSize: '0.66rem', color: '#8a908c', marginBottom: 3, whiteSpace: 'nowrap' }}>
                      {bar.v > 0 ? compactCurrency(bar.v) : ''}
                    </div>
                    <div
                      title={formatCurrency(bar.v)}
                      style={{
                        width: '100%',
                        height: Math.max(bar.v > 0 ? 2 : 0, barPx(bar.v)),
                        backgroundColor: bar.c,
                        borderRadius: '4px 4px 0 0',
                        transition: 'height 200ms ease-out',
                      }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b716d', fontWeight: 600, marginTop: 8, borderTop: '1px solid #e3e7e4', paddingTop: 6, width: '100%', textAlign: 'center' }}>
                {b.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Aging matrix (Insurance / Patient / Total AR × buckets)
// ---------------------------------------------------------------------------

const AgingMatrix = ({ totals }) => {
  const rows = [
    { key: 'insuranceArBuckets', label: 'Insurance AR', total: totals.insuranceAr, color: '#a8520c' },
    { key: 'patientArBuckets', label: 'Patient AR', total: totals.patientAr, color: '#b42318' },
    { key: 'totalArBuckets', label: 'Total AR', total: totals.totalAr, color: '#16241c', bold: true },
  ];
  const grandTotal = Number(totals.totalAr || 0);

  return (
    <div style={{ overflowX: 'auto', border: '1px solid #e3e7e4', borderRadius: 12, backgroundColor: '#fff' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
        <thead>
          <tr>
            <th style={thLeft}>Aging bucket</th>
            {BUCKETS.map((b) => (
              <th key={b.key} style={th}>{b.label}</th>
            ))}
            <th style={{ ...th, borderLeft: '1px solid #e3e7e4' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const buckets = totals[r.key] || {};
            return (
              <tr key={r.key}>
                <td style={{ ...tdLeft, fontWeight: r.bold ? 700 : 600, color: r.color }}>
                  {r.label}
                </td>
                {BUCKETS.map((b) => (
                  <td key={b.key} style={{ ...td, fontWeight: r.bold ? 700 : 400 }}>
                    {formatCurrency(buckets[b.key])}
                  </td>
                ))}
                <td style={{ ...td, borderLeft: '1px solid #e3e7e4', fontWeight: 700, color: r.color }}>
                  {formatCurrency(r.total)}
                </td>
              </tr>
            );
          })}
          {/* % of total AR distribution row */}
          <tr>
            <td style={{ ...tdLeft, color: '#8a908c', fontStyle: 'italic' }}>% of total AR</td>
            {BUCKETS.map((b) => (
              <td key={b.key} style={{ ...td, color: '#8a908c' }}>
                {pct((totals.totalArBuckets || {})[b.key], grandTotal)}%
              </td>
            ))}
            <td style={{ ...td, borderLeft: '1px solid #e3e7e4', color: '#8a908c' }}>100%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Households table
// ---------------------------------------------------------------------------

const InvoiceBadge = ({ invoices, onClick }) => {
  const meta = badgeMetaForInvoices(invoices);
  if (!meta) return null;
  return (
    <button
      type="button"
      title={`${meta.count} invoice${meta.count === 1 ? '' : 's'} — click to view timeline`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        marginLeft: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 7px',
        borderRadius: 999,
        border: `1px solid ${meta.color}`,
        backgroundColor: '#fff',
        color: meta.color,
        fontSize: '0.72rem',
        fontWeight: 700,
        cursor: 'pointer',
        lineHeight: 1.4,
        verticalAlign: 'middle',
      }}
    >
      <span aria-hidden style={{ fontSize: '0.8rem' }}>🧾</span>
      {meta.count > 1 ? meta.count : ''}
    </button>
  );
};

const PlanBadge = ({ plans, onClick }) => {
  const meta = badgeMetaForPlans(plans);
  if (!meta) return null;
  return (
    <button
      type="button"
      title={`${meta.count} payment plan${meta.count === 1 ? '' : 's'} — click for details`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        marginLeft: 6,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 7px',
        borderRadius: 999,
        border: `1px solid ${meta.color}`,
        backgroundColor: '#fff',
        color: meta.color,
        fontSize: '0.72rem',
        fontWeight: 700,
        cursor: 'pointer',
        lineHeight: 1.4,
        verticalAlign: 'middle',
      }}
    >
      <span aria-hidden style={{ fontSize: '0.8rem' }}>📅</span>
      {meta.count > 1 ? meta.count : ''}
    </button>
  );
};

const HouseholdRow = ({ hh, expanded, onToggle, invoices, onOpenInvoices, plans, onOpenPlans, onSendInvoice }) => {
  // Hide "Send invoice" when the household is on an active payment plan — the
  // balance is already being collected in installments, so invoicing on top of
  // the plan would double-bill.
  const patientAr = Number(hh.patientAr || 0);
  // "On plan" (hide the invoice button) requires an ACTIVE plan whose scope
  // covers the current patient AR. Because installment charges post to the
  // ledger over time, the same enrolled patient can legitimately read as
  // either:
  //   • the plan's REMAINING balance  (installmentsRemaining × amount), once
  //     earlier installments have posted, or
  //   • the plan's ORIGINAL total     (totalAmountCents), before any post.
  // Andrea Rowland is the worked example: $937.50 remaining on a $1,250 plan —
  // her AR shows $1,250. We treat the AR as covered whenever it's at or below
  // the plan's ceiling (the greater of original total and remaining, so a
  // missing field on one still counts). Only a balance ABOVE that ceiling is
  // genuinely uncovered (new charges beyond the plan) and warrants an invoice.
  const activePlans = (plans || []).filter(
    (p) => String(p.status || '').toLowerCase() === 'active'
  );
  const planRemaining = activePlans.reduce(
    (s, p) => s + (Number(p.installmentsRemaining || 0) * Number(p.installmentAmountCents || 0)) / 100,
    0
  );
  const planOriginalTotal = activePlans.reduce(
    (s, p) => s + Number(p.totalAmountCents || 0) / 100,
    0
  );
  // $1 slack absorbs cent-level rounding between the ledger and the plan.
  const planCeiling = Math.max(planRemaining, planOriginalTotal);
  const coveredByPlan =
    activePlans.length > 0 && patientAr > 0 && patientAr <= planCeiling + 1;

  // Only offer "Send invoice" when the household actually owes a *patient*
  // balance. A household whose AR is entirely pending-insurance (patientAr = 0)
  // has nothing the patient can pay yet — invoicing them would be wrong — so we
  // suppress the button and just show a dash. Rounded to cents to ignore
  // sub-penny float noise.
  const hasPatientAr = Math.round(patientAr * 100) > 0;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7f8f7')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <td style={tdLeft}>
          <span style={{ color: '#8a908c', marginRight: 8, display: 'inline-block', width: 10 }}>
            {expanded ? '▾' : '▸'}
          </span>
          <span data-phi>{hh.name}</span>
          <InvoiceBadge invoices={invoices} onClick={() => onOpenInvoices(hh, invoices)} />
          <PlanBadge plans={plans} onClick={() => onOpenPlans(hh, plans)} />
        </td>
        <td style={td}>{formatCurrency(hh.patientAr)}</td>
        <td style={{ ...td, color: hh.insuranceAr > 0 ? '#a8520c' : '#8a908c' }}>
          {formatCurrency(hh.insuranceAr)}
        </td>
        <td style={{ ...td, fontWeight: 700 }}>{formatCurrency(hh.totalAr)}</td>
        <td style={{ ...td, color: hh.pastDuePatientAr > 0 ? '#b42318' : '#8a908c' }}>
          {formatCurrency(hh.pastDuePatientAr)}
        </td>
        <td style={{ ...td, textAlign: 'center' }}>
          {coveredByPlan ? (
            <span
              title="On an active payment plan — balance is being collected in installments"
              style={{ fontSize: '0.78rem', color: '#1d6b40', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              📅 On plan
            </span>
          ) : !hasPatientAr ? (
            <span
              title="No patient balance to invoice — this household's AR is pending insurance only"
              style={{ color: '#cdd3cf' }}
            >
              —
            </span>
          ) : (
            // Same button as the Ledger's row-level Invoice action.
            <button
              type="button"
              className="pm-btn-primary pm-btn-sm"
              onClick={(e) => { e.stopPropagation(); onSendInvoice(hh); }}
              title="Create and send an invoice to this household"
            >
              <Icon name="send" size={14} strokeWidth={2} />
              Invoice
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: '0 12px 14px 30px', backgroundColor: '#f7f8f7', borderBottom: '1px solid #eef0ee' }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 10, alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', gap: 20, alignItems: 'stretch' }}>
                <BucketMini title="Patient AR by age" buckets={hh.patientBuckets} />
                <div style={{ width: 1, backgroundColor: '#e3e7e4', alignSelf: 'stretch' }} />
                <BucketMini title="Insurance AR by age" buckets={hh.insuranceBuckets} />
              </div>
              <div style={{ fontSize: '0.75rem', color: '#8a908c', marginLeft: 'auto' }}>
                Household&nbsp;#<span data-phi>{hh.householdHeadId}</span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const BucketMini = ({ title, buckets }) => (
  <div>
    <div style={{ fontSize: '0.72rem', color: '#6b716d', fontWeight: 600, marginBottom: 4 }}>{title}</div>
    <div style={{ display: 'flex', gap: 12 }}>
      {BUCKETS.map((b) => (
        <div key={b.key} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.68rem', color: '#8a908c' }}>{b.label}</div>
          <div style={{ fontSize: '0.85rem', color: '#16241c' }}>
            {formatCurrency((buckets || {})[b.key])}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Invoice timeline modal
// ---------------------------------------------------------------------------

const shortInvoiceId = (id) => {
  const s = String(id || '');
  return s.length > 8 ? `#${s.slice(-8)}` : `#${s}`;
};

const InvoiceTimeline = ({ invoice }) => {
  const timeline = buildInvoiceTimeline(invoice);
  return (
    <div style={{ paddingLeft: 2, marginTop: 10 }}>
      {timeline.map((activity, index) => {
        const isLast = index === timeline.length - 1;
        const color = dotColorForAction(activity.action);
        return (
          <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 14 }}>
              <div style={{
                width: 11, height: 11, borderRadius: '50%',
                backgroundColor: color, border: '2px solid #fff',
                boxShadow: `0 0 0 2px ${color}`,
              }} />
              {!isLast && (
                <div style={{ width: 2, minHeight: 34, flex: 1, backgroundColor: '#e3e7e4', margin: '3px 0' }} />
              )}
            </div>
            <div style={{ flex: 1, paddingBottom: isLast ? 0 : 18 }}>
              <div style={{ fontWeight: 600, color: '#16241c', fontSize: '0.88rem' }}>
                {getActionLabel(activity.action)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b716d', marginTop: 1 }}>
                {formatDateTime(activity.timestamp)}
              </div>
              {activity.details?.refusalDescription && (
                <div style={{ fontSize: '0.75rem', color: '#b42318', marginTop: 2 }}>
                  {activity.details.refusalDescription}
                  {activity.details.refusalCode ? ` (${activity.details.refusalCode})` : ''}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InvoiceTimelineModal = ({ household, invoices, onClose }) => {
  // Newest invoice first.
  const sorted = useMemo(
    () => [...(invoices || [])].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    ),
    [invoices]
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '48px 16px', zIndex: 1000, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
          boxShadow: '0 20px 45px rgba(15,23,42,0.25)', overflow: 'hidden',
          textAlign: 'left',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid #e3e7e4',
        }}>
          <div>
            <div data-phi style={{ fontSize: '1.05rem', fontWeight: 700, color: '#16241c' }}>
              {household?.name}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b716d', marginTop: 2 }}>
              {sorted.length} invoice{sorted.length === 1 ? '' : 's'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none', background: 'transparent', fontSize: '1.4rem',
              lineHeight: 1, color: '#8a908c', cursor: 'pointer',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ padding: '8px 20px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
          {sorted.length === 0 && (
            <div style={{ padding: 20, color: '#8a908c', fontSize: '0.9rem' }}>
              No invoices found for this household.
            </div>
          )}
          {sorted.map((inv) => {
            const sm = statusMeta(inv.status);
            return (
              <div
                key={inv.id}
                style={{
                  border: '1px solid #e3e7e4', borderRadius: 10,
                  padding: '14px 16px', marginTop: 14,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#16241c', fontSize: '0.95rem' }}>
                      {formatCurrency(inv.total)}
                      <span style={{ color: '#8a908c', fontWeight: 500, fontSize: '0.8rem', marginLeft: 8 }}>
                        {shortInvoiceId(inv.id)}
                      </span>
                    </div>
                    {inv.description && (
                      <div style={{ fontSize: '0.78rem', color: '#6b716d', marginTop: 2 }}>
                        {inv.description}
                      </div>
                    )}
                  </div>
                  <span style={{
                    backgroundColor: sm.bg, color: sm.color, borderRadius: 999,
                    padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>
                    {sm.label}
                  </span>
                </div>
                <InvoiceTimeline invoice={inv} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PlanTimeline = ({ plan }) => {
  const log = [...(Array.isArray(plan.activityLog) ? plan.activityLog : [])]
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  if (!log.length) {
    return <div style={{ fontSize: '0.8rem', color: '#8a908c', marginTop: 8 }}>No activity recorded yet.</div>;
  }
  return (
    <div style={{ paddingLeft: 2, marginTop: 10 }}>
      {log.map((activity, index) => {
        const isLast = index === log.length - 1;
        const color = planDotColor(activity.action);
        return (
          <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 14 }}>
              <div style={{
                width: 11, height: 11, borderRadius: '50%',
                backgroundColor: color, border: '2px solid #fff',
                boxShadow: `0 0 0 2px ${color}`,
              }} />
              {!isLast && (
                <div style={{ width: 2, minHeight: 34, flex: 1, backgroundColor: '#e3e7e4', margin: '3px 0' }} />
              )}
            </div>
            <div style={{ flex: 1, paddingBottom: isLast ? 0 : 18 }}>
              <div style={{ fontWeight: 600, color: '#16241c', fontSize: '0.88rem' }}>
                {getPlanActionLabel(activity.action)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b716d', marginTop: 1 }}>
                {formatDateTime(activity.timestamp)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PlanFact = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.68rem', color: '#8a908c', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '0.9rem', color: '#16241c', marginTop: 2 }}>{value}</div>
  </div>
);

const PaymentPlanModal = ({ household, plans, onClose }) => {
  // Active/paused first, then by newest.
  const sorted = useMemo(() => {
    const rank = (s) => (DEAD_PLAN_STATES.has(String(s || '').toLowerCase()) ? 1 : 0);
    return [...(plans || [])].sort((a, b) => {
      const r = rank(a.status) - rank(b.status);
      if (r !== 0) return r;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [plans]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '48px 16px', zIndex: 1000, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
          boxShadow: '0 20px 45px rgba(15,23,42,0.25)', overflow: 'hidden',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e3e7e4' }}>
          <div>
            <div data-phi style={{ fontSize: '1.05rem', fontWeight: 700, color: '#16241c' }}>
              {household?.name}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b716d', marginTop: 2 }}>
              {sorted.length} payment plan{sorted.length === 1 ? '' : 's'}
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', lineHeight: 1, color: '#8a908c', cursor: 'pointer' }} aria-label="Close">×</button>
        </div>

        <div style={{ padding: '8px 20px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
          {sorted.length === 0 && (
            <div style={{ padding: 20, color: '#8a908c', fontSize: '0.9rem' }}>
              No payment plans found for this household.
            </div>
          )}
          {sorted.map((plan) => {
            const sm = planStatusMeta(plan.status);
            const done = Number(plan.installmentsCompleted || 0);
            const total = Number(plan.installments || 0);
            const remainingCents = (Number(plan.installmentsRemaining || 0)) * (Number(plan.installmentAmountCents || 0));
            return (
              <div key={plan.id} style={{ border: '1px solid #e3e7e4', borderRadius: 10, padding: '14px 16px', marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ fontWeight: 700, color: '#16241c', fontSize: '0.95rem' }}>
                    {centsToUsd(plan.installmentAmountCents)}/mo
                    <span style={{ color: '#8a908c', fontWeight: 500, fontSize: '0.8rem', marginLeft: 8 }}>
                      of {centsToUsd(plan.totalAmountCents)} total
                    </span>
                  </div>
                  <span style={{ backgroundColor: sm.bg, color: sm.color, borderRadius: 999, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {sm.label}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 12 }}>
                  <PlanFact label="Progress" value={`${done} of ${total} paid`} />
                  <PlanFact label="Remaining" value={centsToUsd(remainingCents)} />
                  <PlanFact
                    label="Next charge"
                    value={DEAD_PLAN_STATES.has(String(plan.status || '').toLowerCase())
                      ? '—'
                      : formatDate(plan.nextChargeDate)}
                  />
                </div>

                <PlanTimeline plan={plan} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

const downloadHouseholdsCsv = (practiceId, asOf, households) => {
  const header = [
    'Household', 'HouseholdHead Chart ID', 'Patient AR', 'Insurance AR', 'Total AR',
    'Patient 0-30', 'Patient 31-60', 'Patient 61-90', 'Patient 90+',
    'Insurance 0-30', 'Insurance 31-60', 'Insurance 61-90', 'Insurance 90+',
    'Notes',
  ];
  const esc = (v) => {
    const s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(',')];
  households.forEach((hh) => {
    const p = hh.patientBuckets || {};
    const i = hh.insuranceBuckets || {};
    lines.push([
      // Prefer the PMS chart number; fall back to the internal head id for
      // snapshots computed before headChartId was added.
      esc(hh.name), esc(hh.headChartId || hh.householdHeadId),
      hh.patientAr, hh.insuranceAr, hh.totalAr,
      p.d0_30 || 0, p.d31_60 || 0, p.d61_90 || 0, p.d90_plus || 0,
      i.d0_30 || 0, i.d31_60 || 0, i.d61_90 || 0, i.d90_plus || 0,
      '', // Notes — intentionally blank for staff to annotate
    ].join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ar-aging_${practiceId}_${asOf || 'current'}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

const ARAgingTab = ({ practiceId }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  // null = all ages; otherwise one of BUCKETS[].key (d0_30 / d31_60 / …).
  const [bucketFilter, setBucketFilter] = useState(null);
  // null = both; 'patient' or 'insurance' to scope to one receivable.
  const [arTypeFilter, setArTypeFilter] = useState(null);
  // { household, invoices } while the timeline modal is open.
  const [invoiceModal, setInvoiceModal] = useState(null);
  // { household, plans } while the payment-plan modal is open.
  const [planModal, setPlanModal] = useState(null);
  // The household we're sending an invoice for. When set we mount a headless
  // LedgerTab (modalOnly) that opens the exact same invoice-preview popup as
  // the Ledger page, in place — no tab switch.
  const [invoiceFor, setInvoiceFor] = useState(null);
  // Large practices are served nightly-only: no snapshot yet => pending;
  // Refresh only re-reads the stored doc (no live recompute).
  const [pending, setPending] = useState(false);
  const [refreshDisabled, setRefreshDisabled] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (!practiceId) return;
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      // Invoices + payment plans are fetched live (not from the nightly
      // snapshot) so freshly created ones show immediately. A failure in
      // either is non-fatal — the aging table still renders, just without
      // that badge.
      const [resp, invResp, planResp] = await Promise.all([
        getArAging(practiceId, { refresh }),
        getInvoices(practiceId).catch((e) => {
          console.error('AR Aging: invoice fetch failed', e);
          return null;
        }),
        listPaymentPlans(practiceId).catch((e) => {
          console.error('AR Aging: payment-plan fetch failed', e);
          return null;
        }),
      ]);
      if (!resp?.success) {
        setError(resp?.error || 'Failed to load AR aging.');
        setSnapshot(null);
      } else {
        setSnapshot(resp.snapshot || null);
        setPending(!!resp.pending);
        setRefreshDisabled(!!resp.refreshDisabled);
      }
      setInvoices(Array.isArray(invResp?.invoices) ? invResp.invoices : []);
      setPlans(Array.isArray(planResp?.data) ? planResp.data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load AR aging.');
      setSnapshot(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [practiceId]);

  // Refresh only the live invoice list (used after the send-invoice popup
  // closes) so the 🧾 badge reflects a freshly created invoice without
  // recomputing the whole snapshot.
  const reloadInvoices = useCallback(async () => {
    if (!practiceId) return;
    try {
      const r = await getInvoices(practiceId);
      setInvoices(Array.isArray(r?.invoices) ? r.invoices : []);
    } catch (e) {
      console.error('AR Aging: invoice reload failed', e);
    }
  }, [practiceId]);

  useEffect(() => {
    setExpanded({});
    setSearch('');
    setBucketFilter(null);
    setArTypeFilter(null);
    load(false);
  }, [load]);

  const households = snapshot?.households || [];

  // Index live invoices by their patientId (a bare Kolla contact id), then
  // roll them up per household via the snapshot's memberIds. One contact
  // belongs to one household, so there's no double counting.
  const invoicesByHousehold = useMemo(() => {
    const byPatient = new Map();
    for (const inv of invoices) {
      const pid = String(inv.patientId || '');
      if (!pid) continue;
      if (!byPatient.has(pid)) byPatient.set(pid, []);
      byPatient.get(pid).push(inv);
    }
    const out = {};
    for (const hh of households) {
      const ids = hh.memberIds && hh.memberIds.length
        ? hh.memberIds
        : [hh.householdHeadId];
      const list = [];
      for (const id of ids) {
        const hit = byPatient.get(String(id));
        if (hit) list.push(...hit);
      }
      if (list.length) out[hh.householdHeadId] = list;
    }
    return out;
  }, [invoices, households]);

  // Same rollup for payment plans (plan.patientId is a bare contact id).
  const plansByHousehold = useMemo(() => {
    const byPatient = new Map();
    for (const p of plans) {
      const pid = String(p.patientId || '');
      if (!pid) continue;
      if (!byPatient.has(pid)) byPatient.set(pid, []);
      byPatient.get(pid).push(p);
    }
    const out = {};
    for (const hh of households) {
      const ids = hh.memberIds && hh.memberIds.length
        ? hh.memberIds
        : [hh.householdHeadId];
      const list = [];
      for (const id of ids) {
        const hit = byPatient.get(String(id));
        if (hit) list.push(...hit);
      }
      if (list.length) out[hh.householdHeadId] = list;
    }
    return out;
  }, [plans, households]);

  // Amount a household has aging in a given bucket, scoped to the selected AR
  // type (patient / insurance / both).
  const bucketAmount = (h, key) => {
    const p = Number((h.patientBuckets || {})[key] || 0);
    const i = Number((h.insuranceBuckets || {})[key] || 0);
    if (arTypeFilter === 'patient') return p;
    if (arTypeFilter === 'insurance') return i;
    return p + i;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = households.filter((h) => {
      // Households carry no phone/email — match name, the head's patient
      // id, the head's PMS chart id, and every member's patient id.
      if (q
        && !(h.name || '').toLowerCase().includes(q)
        && !String(h.householdHeadId || '').includes(q)
        && !String(h.headChartId || '').toLowerCase().includes(q)
        && !(h.memberIds || []).some((id) => String(id).includes(q))) {
        return false;
      }
      if (arTypeFilter === 'patient' && Math.round(Number(h.patientAr || 0) * 100) === 0) {
        return false;
      }
      if (arTypeFilter === 'insurance' && Math.round(Number(h.insuranceAr || 0) * 100) === 0) {
        return false;
      }
      if (bucketFilter && Math.round(bucketAmount(h, bucketFilter) * 100) === 0) {
        return false;
      }
      return true;
    });
    // When a timeframe is selected, surface the largest balances in that
    // bucket first (rather than the default total-AR order).
    if (bucketFilter) {
      return [...rows].sort(
        (a, b) => bucketAmount(b, bucketFilter) - bucketAmount(a, bucketFilter)
      );
    }
    return rows;
  }, [households, search, bucketFilter, arTypeFilter]);

  if (loading) {
    return <div style={{ padding: 40, color: '#6b716d' }}>Loading AR aging…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ padding: 16, backgroundColor: '#fef5f4', border: '1px solid #f5c6c1', borderRadius: 10, color: '#b42318' }}>
          {error}
        </div>
        <button onClick={() => load(false)} style={primaryBtn}>Retry</button>
      </div>
    );
  }

  if (pending && !snapshot) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ padding: 18, backgroundColor: '#fef9f1', border: '1px solid #f3d9ae', borderRadius: 10, color: '#a8520c', maxWidth: 640 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Snapshot not computed yet</div>
          AR aging for this practice is generated on a nightly schedule (its
          ledger is too large to compute on demand). It will appear here after
          the next scheduled run.
        </div>
        <button onClick={() => load(false)} style={{ ...secondaryBtn, marginTop: 12 }}>↻ Check again</button>
      </div>
    );
  }

  const totals = snapshot?.totals || {};

  return (
    <div style={{ width: '100%' }}>
      {/* Header / meta / actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div style={{ color: '#6b716d', fontSize: '0.85rem' }}>
          Snapshot as of <strong style={{ color: '#16241c' }}>{formatDate(snapshot?.asOf)}</strong>
          {' · '}last computed {formatDateTime(snapshot?.generatedAt)}
          {snapshot?.householdsTruncated && (
            <span style={{ marginLeft: 8, color: '#a8520c' }}>
              (showing top {households.length} of {snapshot?.householdsTotal})
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadHouseholdsCsv(practiceId, snapshot?.asOf, filtered)}
            disabled={!filtered.length}
            style={secondaryBtn}
            title={
              (bucketFilter || arTypeFilter || search.trim())
                ? `Export the ${filtered.length} filtered household(s)`
                : `Export all ${filtered.length} household(s)`
            }
          >
            {(bucketFilter || arTypeFilter || search.trim())
              ? `⬇ Export filtered rows (${filtered.length})`
              : '⬇ Export CSV'}
          </button>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            style={primaryBtn}
            title={refreshDisabled
              ? 'Re-reads the latest nightly snapshot (live recompute is disabled for this large practice).'
              : 'Recompute the snapshot now.'}
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* KPI strip — same shared tiles as the Ledger and Invoices tabs.
          Each tile's tooltip explains how the figure is derived. */}
      <div className="pm-kpis pm-kpis-dense" style={{ marginBottom: 22 }}>
        <KpiTile
          label="Total AR"
          value={formatCurrency(totals.totalAr)}
          sub={`${totals.householdsWithBalance || 0} households with a balance`}
          title="Everything patients + insurance owe the practice."
        />
        <KpiTile
          label="Est. patient"
          value={formatCurrency(totals.patientAr)}
          valueColor={totals.patientAr > 0 ? 'var(--pm-danger)' : undefined}
          sub="Ledger balance − outstanding insurance"
          title="True patient responsibility after outstanding insurance is removed."
        />
        <KpiTile
          label="Est. insurance"
          value={formatCurrency(totals.insuranceAr)}
          valueColor={totals.insuranceAr > 0 ? 'var(--pm-warn)' : undefined}
          sub="Pending claims, capped at account balance"
          title="Money carriers still owe on pending, non-phantom claims — capped at each account's balance so stale estimates don't inflate AR."
        />
        <KpiTile
          label="Past due"
          value={formatCurrency(totals.pastDuePatientAr)}
          valueColor={totals.pastDuePatientAr > 0 ? 'var(--pm-danger)' : undefined}
          sub="Patient AR older than 30 days"
          title="Patient balances aged beyond the 0–30 bucket."
        />
        <KpiTile
          label="Patients past due"
          value={String(totals.patientsPastDue || 0)}
          sub="Households with 30+ day patient AR"
        />
        <KpiTile
          label="Credit balances"
          value={formatCurrency(totals.creditBalanceTotal)}
          valueColor={totals.creditBalanceTotal > 0 ? 'var(--pm-ok)' : undefined}
          sub={`${totals.householdsWithCredit || 0} households in credit`}
          title="Households the practice owes money back to."
        />
      </div>

      {/* Aging distribution chart */}
      <h3 style={sectionHeading}>Aging distribution</h3>
      <div style={{ marginBottom: 26, overflowX: 'auto' }}>
        <AgingBarChart totals={totals} />
      </div>

      {/* Aging matrix */}
      <h3 style={sectionHeading}>Aging breakdown</h3>
      <div style={{ marginBottom: 26 }}>
        <AgingMatrix totals={totals} />
      </div>

      {/* Households */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ ...sectionHeading, marginBottom: 0 }}>
          Households ({filtered.length})
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* AR type filter */}
          <div style={{ display: 'inline-flex', border: '1px solid #e3e7e4', borderRadius: 8, overflow: 'hidden' }}>
            {[
              { key: null, label: 'All AR' },
              { key: 'patient', label: 'Patient' },
              { key: 'insurance', label: 'Insurance' },
            ].map((opt, i) => {
              const active = arTypeFilter === opt.key;
              return (
                <button
                  key={opt.key || 'all'}
                  type="button"
                  onClick={() => setArTypeFilter(opt.key)}
                  title={opt.key ? `Households with ${opt.label} AR` : 'Patient + insurance AR'}
                  style={{
                    padding: '7px 12px',
                    border: 'none',
                    borderLeft: i === 0 ? 'none' : '1px solid #e3e7e4',
                    backgroundColor: active ? '#1f5a3d' : '#fff',
                    color: active ? '#fff' : '#3c4540',
                    fontSize: '0.82rem',
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Age bucket filter */}
          <div style={{ display: 'inline-flex', border: '1px solid #e3e7e4', borderRadius: 8, overflow: 'hidden' }}>
            {[{ key: null, label: 'All ages' }, ...BUCKETS].map((opt, i) => {
              const active = bucketFilter === opt.key;
              return (
                <button
                  key={opt.key || 'all'}
                  type="button"
                  onClick={() => setBucketFilter(opt.key)}
                  title={opt.key ? `Households with AR aged ${opt.label}` : 'All households'}
                  style={{
                    padding: '7px 12px',
                    border: 'none',
                    borderLeft: i === 0 ? 'none' : '1px solid #e3e7e4',
                    backgroundColor: active ? '#1f5a3d' : '#fff',
                    color: active ? '#fff' : '#3c4540',
                    fontSize: '0.82rem',
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Household search — same bar as the Invoices / Payment Plans tabs. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: 12,
        flexWrap: 'wrap'
      }}>
        <div style={{
          position: 'relative',
          flex: '0 1 360px',
          minWidth: '220px'
        }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by household name, patient ID, or chart ID"
            aria-label="Filter households"
            style={{
              width: '100%',
              padding: '8px 32px 8px 12px',
              border: '1px solid #d6dbd7',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear filter"
              title="Clear filter"
              style={{
                position: 'absolute',
                right: '6px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#6b716d',
                fontSize: '16px',
                lineHeight: 1,
                padding: '4px 6px',
                borderRadius: '4px'
              }}
            >
              ✕
            </button>
          )}
        </div>
        {search && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: '#f1ecfb',
              color: '#5b3fa8',
              border: '1px solid #ddd2f3',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              lineHeight: 1.4
            }}
          >
            Showing {filtered.length} of {households.length}
          </span>
        )}
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid #d6dbd7',
              borderRadius: '4px',
              backgroundColor: 'white',
              color: '#3c4540',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear
          </button>
        )}
      </div>

      {(bucketFilter || arTypeFilter) && (
        <div style={{ fontSize: '0.8rem', color: '#6b716d', marginTop: 8 }}>
          Showing{' '}
          <strong style={{ color: '#16241c' }}>
            {arTypeFilter === 'patient' ? 'patient' : arTypeFilter === 'insurance' ? 'insurance' : 'all'}
          </strong>
          {' AR'}
          {bucketFilter && (
            <>
              {' aged '}
              <strong style={{ color: '#16241c' }}>
                {BUCKETS.find((b) => b.key === bucketFilter)?.label}
              </strong>
            </>
          )}
          {' · '}
          <button
            type="button"
            onClick={() => { setBucketFilter(null); setArTypeFilter(null); }}
            style={{ border: 'none', background: 'transparent', color: '#1f5a3d', cursor: 'pointer', padding: 0, fontSize: '0.8rem', fontWeight: 600 }}
          >
            Clear
          </button>
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e3e7e4', borderRadius: 12, backgroundColor: '#fff', marginTop: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 840 }}>
          <thead>
            <tr>
              <th style={thLeft}>Household</th>
              <th style={th}>Patient AR</th>
              <th style={th}>Insurance AR</th>
              <th style={th}>Total AR</th>
              <th style={th}>Past Due (30+)</th>
              <th style={{ ...th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...tdLeft, color: '#8a908c', padding: 24 }}>
                  {bucketFilter || arTypeFilter || search.trim()
                    ? 'No households match the current filters.'
                    : 'No households with an outstanding balance.'}
                </td>
              </tr>
            )}
            {filtered.map((hh) => (
              <HouseholdRow
                key={hh.householdHeadId}
                hh={hh}
                invoices={invoicesByHousehold[hh.householdHeadId]}
                onOpenInvoices={(household, invs) =>
                  setInvoiceModal({ household, invoices: invs || [] })
                }
                plans={plansByHousehold[hh.householdHeadId]}
                onOpenPlans={(household, pls) =>
                  setPlanModal({ household, plans: pls || [] })
                }
                onSendInvoice={(household) => setInvoiceFor({ ...household, _ts: Date.now() })}
                expanded={!!expanded[hh.householdHeadId]}
                onToggle={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [hh.householdHeadId]: !prev[hh.householdHeadId],
                  }))
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: '0.75rem', color: '#8a908c', marginTop: 14, lineHeight: 1.5 }}>
        Total AR equals the ledger balance: insurance AR (aged by claim date, capped at
        each account's balance) plus patient AR (aged by transaction date, FIFO). Totals
        are exact; the per-bucket patient/insurance split is a claim-level approximation.
        Snapshot recomputes nightly. Next to each household, the 🧾 badge shows
        live invoices (click for the timeline) and the 📅 badge shows payment
        plans (click for progress + activity).
      </p>

      {invoiceModal && (
        <InvoiceTimelineModal
          household={invoiceModal.household}
          invoices={invoiceModal.invoices}
          onClose={() => setInvoiceModal(null)}
        />
      )}

      {planModal && (
        <PaymentPlanModal
          household={planModal.household}
          plans={planModal.plans}
          onClose={() => setPlanModal(null)}
        />
      )}

      {/* Headless Ledger instance: renders only the invoice-preview popup (the
          exact same flow as the Ledger page) in place, so staff never leave
          the AR Aging board. Unmounts + refreshes the invoice badges on close. */}
      {invoiceFor && (
        <LedgerTab
          practiceId={practiceId}
          modalOnly
          // No onRequestCharge on purpose: the AR Aging invoice popup offers
          // "Create invoice" + "Send link" only. "Create & charge" is hidden
          // here because it would navigate off to the Invoices tab.
          openInvoiceRequest={{
            contactId: invoiceFor.householdHeadId,
            name: invoiceFor.name || '',
            ts: invoiceFor._ts,
          }}
          onClose={() => {
            setInvoiceFor(null);
            reloadInvoices();
          }}
        />
      )}

    </div>
  );
};

const sectionHeading = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#16241c',
  margin: '0 0 12px 0',
};

const primaryBtn = {
  padding: '9px 16px',
  backgroundColor: '#1f5a3d',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
};

const secondaryBtn = {
  padding: '9px 16px',
  backgroundColor: '#fff',
  color: '#3c4540',
  border: '1px solid #e3e7e4',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
};

export default ARAgingTab;
